import { IconPlayArrow } from '@/app/components/icons/audio-video/icon-play-arrow';
import { IconCamera } from '@/app/components/icons/photo-image/icon-camera';
import { IconView360 } from '@/app/components/icons/photo-image/icon-view-360';
import { IconEngine } from '@/app/components/icons/transportation/icon-engine';
import { IconSeat } from '@/app/components/icons/transportation/icon-seat';
import { IconWheel } from '@/app/components/icons/transportation/icon-wheel';
import { IconSettings } from '@/app/components/icons/ui-actions/icon-settings';
import type { CompareListType } from '@/app/lcv/components/sections/compare/types';

export const META_DATA = {
  title: 'เปรียบเทียบรุ่นรถ',
  description:
    'เลือกรุ่นรถที่ต้องการเปรียบเทียบรายละเอียดทางเทคนิคและอุปกรณ์ต่างๆ',
};
export const HEADLINE = {
  title: 'เปรียบเทียบรุ่นรถ',
  description:
    'เลือกรุ่นรถที่ต้องการเปรียบเทียบรายละเอียดทางเทคนิคและอุปกรณ์ต่างๆ',
};

export const COMPARE_SUMMARY: CompareListType = {
  title: 'รายละเอียดสำคัญ',
  fieldKey: 'summary',
  items: [
    { fieldKey: 'summaryEngine', title: 'เครื่องยนต์', icon: <IconEngine /> },
    {
      fieldKey: 'summaryMaxTorque',
      title: 'แรงบิด',
      icon: <IconSettings />,
    },
    { fieldKey: 'summaryWheel', title: 'ล้อ', icon: <IconWheel /> },
    {
      fieldKey: 'summarySeatMaterial',
      title: 'วัสดุเบาะนั่ง',
      icon: <IconSeat />,
    },
    {
      fieldKey: 'summaryAudioSystem',
      title: 'ระบบเครื่องเสียง',
      icon: <IconPlayArrow />,
    },
    {
      fieldKey: 'summaryRearViewCamera',
      title: 'กล้องมองภาพด้านหลังขณะถอยจอด',
      icon: <IconCamera />,
    },
    {
      fieldKey: 'summary360Camera',
      title: 'กล้องมองรอบคัน 360 องศา พร้อมมุมมองแบบ 3 มิติ',
      icon: <IconView360 />,
    },
  ],
};

export const COMPARE_TOGGLES: CompareListType[] = [
  {
    title: 'เครื่องยนต์',
    fieldKey: 'engine',
    items: [
      { fieldKey: 'engineModel', title: 'รุ่น' },
      { fieldKey: 'engineType', title: 'แบบ' },
      { fieldKey: 'engineDisplacement', title: 'ความจุกระบอกสูบ (ซีซี)' },
      {
        fieldKey: 'engineBoreWidthStroke',
        title: 'ความกว้างกระบอกสูบ x ช่วงชัก (มม.)',
      },
      {
        fieldKey: 'engineCompressionRatio',
        title: 'อัตราส่วนกำลังอัด (ต่อ 1)',
      },
      {
        fieldKey: 'engineMaximumPower',
        title: 'กำลังสูงสุด กิโลวัตต์ (แรงม้า) / รอบต่อนาที',
      },
      {
        fieldKey: 'engineMaximumTorque',
        title: 'แรงบิดสูงสุด (นิวตัน-เมตร / รอบต่อนาที)',
      },
      {
        fieldKey: 'engineIssSystem',
        title: 'ระบบ ISS (Idling Stop / Start System)',
      },
      {
        fieldKey: 'engineFuel',
        title: 'น้ำมันเชื้อเพลิง',
      },
      {
        fieldKey: 'engineFuelTankCapacity',
        title: 'ความจุถังน้ำมัน (ลิตร)',
      },
    ],
  },
  {
    title: 'ระบบส่งกำลัง',
    fieldKey: 'transmission',
    items: [
      { fieldKey: 'transmissionDriveSystem', title: 'ระบบขับเคลื่อน' },
      { fieldKey: 'transmissionClutch', title: 'คลัตช์' },
      { fieldKey: 'transmissionType', title: 'ระบบเกียร์' },
      {
        fieldKey: 'transmissionGear1',
        title: 'อัตราทดเกียร์ 1',
        format: 'gear',
      },
      {
        fieldKey: 'transmissionGear2',
        title: 'อัตราทดเกียร์ 2',
        format: 'gear',
      },
      {
        fieldKey: 'transmissionGear3',
        title: 'อัตราทดเกียร์ 3',
        format: 'gear',
      },
      {
        fieldKey: 'transmissionGear4',
        title: 'อัตราทดเกียร์ 4',
        format: 'gear',
      },
      {
        fieldKey: 'transmissionGear5',
        title: 'อัตราทดเกียร์ 5',
        format: 'gear',
      },
      {
        fieldKey: 'transmissionGear6',
        title: 'อัตราทดเกียร์ 6',
        format: 'gear',
      },
      {
        fieldKey: 'transmissionGear7',
        title: 'อัตราทดเกียร์ 7',
        format: 'gear',
      },
      {
        fieldKey: 'transmissionGear8',
        title: 'อัตราทดเกียร์ 8',
        format: 'gear',
      },
      {
        fieldKey: 'transmissionReverseGear',
        title: 'อัตราทดเกียร์ถอยหลัง',
        format: 'gear',
      },
      {
        fieldKey: 'transmissionFinalGearRatio',
        title: 'อัตราทดเฟืองท้าย',
        format: 'gear',
      },
      {
        fieldKey: 'transmissionTransferGearRatio',
        title: 'อัตราทดเกียร์ 4L/4H',
      },
      {
        fieldKey: 'transmissionRoughTerrainMode',
        title: 'ระบบ Rough Terrain Mode',
      },
      {
        fieldKey: 'transmissionDifferentialGearLock',
        title: 'ระบบ Electronic Diff-Lock ที่เฟืองท้าย',
      },
    ],
  },
  {
    title: 'ระบบพวงมาลัย',
    fieldKey: 'steering',
    items: [
      { fieldKey: 'steeringType', title: 'ชนิด' },
      {
        fieldKey: 'steeringMinTurningRadius',
        title: 'รัศมีวงเลี้ยวแคบสุด (เมตร)',
        format: 'steering',
      },
    ],
  },
  {
    title: 'ระบบเบรก',
    fieldKey: 'brake',
    items: [
      { fieldKey: 'brakeFront', title: 'หน้า' },
      { fieldKey: 'brakeRear', title: 'หลัง' },
    ],
  },
  {
    title: 'ระบบกันสะเทือน',
    fieldKey: 'suspension',
    items: [
      { fieldKey: 'suspensionFront', title: 'หน้า' },
      { fieldKey: 'suspensionRear', title: 'หลัง' },
    ],
  },
  {
    title: 'ล้อและยาง',
    fieldKey: 'wheels',
    items: [
      { fieldKey: 'wheel', title: 'ล้อ' },
      { fieldKey: 'wheelTireSize', title: 'ขนาดยาง' },
      { fieldKey: 'wheelSpareTire', title: 'ยางอะไหล่' },
    ],
  },
  {
    title: 'มิติและความจุ',
    fieldKey: 'dimension',
    items: [
      {
        fieldKey: 'dimensionVehicle',
        title: 'มิติตัวรถ ยาว x กว้าง x สูง (มม.)',
      },
      {
        fieldKey: 'dimensionCargo',
        title: 'มิติกระบะ ยาว x กว้าง x สูง (มม.)',
      },
      { fieldKey: 'dimensionWheelBase', title: 'ฐานล้อ (มม.)' },
      {
        fieldKey: 'dimensionFrontRearTread',
        title: 'ความกว้างช่วงล้อหน้า / หลัง (มม.)',
      },
      {
        fieldKey: 'dimensionMinGroundClearance',
        title: 'ความสูงใต้ท้องรถ (ที่เสื้อเพลาท้าย, มม.)',
      },
      // NOTE: skip 2026/1 ลดการเปรียบเทียบกับคู่แข่ง
      // {
      //   fieldKey: 'dimensionApproxVehicleWeight',
      //   title: 'น้ำหนักรถโดยประมาณ (กก.)',
      // },
    ],
  },
  {
    title: 'อุปกรณ์ภายนอก',
    fieldKey: 'exterior',
    items: [
      { fieldKey: 'exteriorHeadlights', title: 'ไฟหน้า' },
      {
        fieldKey: 'exteriorHeadLightsLevelSystem',
        title: 'ระบบปรับระดับไฟหน้าสูง - ต่ำ',
      },
      { fieldKey: 'exteriorFollowMeHome', title: 'ระบบไฟหน้า Follow me home' },
      {
        fieldKey: 'exteriorTurnOffAutoHeadLights',
        title: 'ระบบเปิด-ปิดไฟหน้าอัตโนมัติ',
      },
      { fieldKey: 'exteriorFrontFogLights', title: 'ไฟตัดหมอกหน้า' },
      { fieldKey: 'exteriorTailLights', title: 'ไฟท้าย' },
      { fieldKey: 'exteriorFrontGrille', title: 'กระจังหน้า' },
      { fieldKey: 'exteriorFrontBumper', title: 'กันชนหน้า' },
      { fieldKey: 'exteriorSideMirror', title: 'กระจกมองข้าง' },
      { fieldKey: 'exteriorDoorHandle', title: 'มือเปิดประตู' },
      { fieldKey: 'exteriorChromeMirrorEdge', title: 'คิ้วขอบกระจก' },
      {
        fieldKey: 'exteriorBPillar',
        title: 'B-Pillar ตกแต่งด้วย Blackout Film',
      },
      { fieldKey: 'exteriorSideSteps', title: 'บันไดข้าง' },
      { fieldKey: 'exteriorRear', title: 'กันชนท้าย' },
      {
        fieldKey: 'tailgateLockKey',
        title: 'กุญแจล็อกฝาท้าย',
      },
      { fieldKey: 'exteriorTwoToneRearSpoiler', title: 'สปอยเลอร์หลัง' },
      { fieldKey: 'exteriorAntenna', title: 'เสาอากาศ' },
      { fieldKey: 'exteriorWindSheild', title: 'กระจกบังลมหน้า' },
      { fieldKey: 'exteriorFrontWiper', title: 'ที่ปัดน้ำฝนด้านหน้า' },
      { fieldKey: 'exteriorRearWiper', title: 'ที่ปัดน้ำฝนด้านหลัง' },
      {
        fieldKey: 'exteriorAutoWiperSystem',
        title: 'ระบบปัดน้ำฝนอัตโนมัติ',
      },
      // { fieldKey: 'exteriorBackRoof', title: 'Black Roof' },
      { fieldKey: 'exteriorRoofTrim', title: 'คิ้วหลังคา' },
      { fieldKey: 'exteriorRoofRail', title: 'ราวหลังคา' },
      {
        fieldKey: 'exteriorStepSensor',
        title: 'ประตูท้ายเปิด-ปิดด้วยระบบไฟฟ้า แบบ Step Sensor',
      },
      { fieldKey: 'exteriorDecorationSet', title: 'ชุดตกแต่ง' },
      {
        fieldKey: 'exteriorXSeriesOnRear',
        title: 'สัญลักษณ์ X-Series ที่ฝากระบะท้าย',
      },
      {
        fieldKey: 'exteriorXseriesOnFender',
        title: 'สัญลักษณ์ X สีแดงที่ Fender',
      },
      {
        fieldKey: 'exteriorRsSign',
        title: 'สัญลักษณ์ RS ที่แก้มข้างสองข้าง,ฝาท้ายด้านขวา',
      },
      {
        fieldKey: 'biturboSign',
        title: 'สัญลักษณ์ Biturbo',
      },
    ],
  },
  {
    title: 'อุปกรณ์ภายใน',
    fieldKey: 'interior',
    items: [
      { fieldKey: 'interiorGauge', title: 'มาตรวัด' },
      {
        fieldKey: 'interiorGearShiftIndicatingSystem',
        title: 'ระบบแนะนำการเปลี่ยนเกียร์ GSI (Genius Sport Shift)',
      },
      { fieldKey: 'interiorSteeringWheelMaterial', title: 'พวงมาลัย' },
      { fieldKey: 'interiorSteeringWheel', title: 'การปรับระดับพวงมาลัย' },
      {
        fieldKey: 'interiorAudioControlOnSteering',
        title: 'สวิตซ์ควบคุมเครื่องเสียงที่พวงมาลัย',
      },
      {
        fieldKey: 'interiorPaddleShift',
        title: 'ระบบควบคุมการเปลี่ยนเกียร์ที่พวงมาลัย (Paddle Shift)',
      },
      {
        fieldKey: 'interiorFrontConsoleDecoration',
        title: 'การตกแต่งคอนโซลหน้า',
      },
      {
        fieldKey: 'interiorMultipurposeHolder',
        title: 'ที่วางของอเนกประสงค์บนคอนโซลหน้า',
      },
      { fieldKey: 'interiorCenterConsole', title: 'คอนโซลกลาง' },
      { fieldKey: 'interiorDoorPanelDecoration', title: 'ชุดตกแต่งแผงประตู' },
      { fieldKey: 'interiorInnerDoorHandle', title: 'มือเปิดประตูด้านใน' },
      { fieldKey: 'interiorPowerWindows', title: 'กระจกไฟฟ้า' },
      {
        fieldKey: 'interiorCentralLocking',
        title: 'เซ็นทรัลล็อก พร้อมระบบล็อกอัตโนมัติเมื่อรถวิ่ง',
      },
      { fieldKey: 'interiorIntegratedRemoteKeyless', title: 'กุญแจรีโมท' },
      {
        fieldKey: 'interiorIsuzuGeniusKeyPushStart',
        title: 'ระบบสตาร์ทแบบ Push Start',
      },
      {
        fieldKey: 'interiorRemoteEngineStart',
        title: 'ระบบ Remote Engine Start',
      },
      { fieldKey: 'interiorAudioSystem', title: 'ระบบเครื่องเสียง' },
      {
        fieldKey: 'interiorTelephoneConnection',
        title: 'ระบบเชื่อมต่อโทรศัพท์ และการเล่นเพลง',
      },
      { fieldKey: 'interiorNoOfSpeakers', title: 'ลำโพง' },
      { fieldKey: 'interiorAirConditioningSystem', title: 'ระบบปรับความเย็น' },
      { fieldKey: 'interiorAirFilter', title: 'แผ่นกรองระบบปรับอากาศ PM 2.5' },
      {
        fieldKey: 'interiorAirVentsRearPassengers',
        title: 'ช่องแอร์สำหรับผู้โดยสารตอนหลัง',
      },
      {
        fieldKey: 'interiorPowerSocket12Volt',
        title: 'ช่องต่ออุปกรณ์ไฟฟ้าเสริม DC 12 โวลต์',
      },
      {
        fieldKey: 'interiorAdditionalUsbPowerOutlet',
        title: 'ช่องต่ออุปกรณ์ไฟฟ้าเสริม USB-C',
      },
      {
        fieldKey: 'interiorGlassesHolderWithLight',
        title: 'ที่ใส่แว่นตา พร้อมไฟส่องแผนที่ 2 ตำแหน่ง',
      },
      { fieldKey: 'interiorSunshade', title: 'ที่บังแดด' },
      { fieldKey: 'interiorLamp', title: 'ไฟส่องสว่างในห้องโดยสาร' },
      {
        fieldKey: 'interiorReadingLightsSeat2',
        title: 'ไฟอ่านหนังสือสำหรับที่นั่งแถวที่ 2: 2 ตำแหน่ง',
      },
      {
        fieldKey: 'interiorRearLuggageLight',
        title: 'ไฟส่องสว่างที่วางสัมภาระด้านท้าย',
      },
      {
        fieldKey: 'interiorAssistAmbientLight',
        title: 'INSTRUMENT PANEL ASSIST SIDE ambient light',
      },
      // { fieldKey: 'interiorFootLight', title: 'ไฟส่องเท้า' },
      { fieldKey: 'interiorSeatModelMaterial', title: 'วัสดุเบาะนั่ง' },
      {
        fieldKey: 'interiorSeatModelFrontCanAdjusted',
        title: 'เบาะนั่งคู่หน้า',
      },
      {
        fieldKey: 'interiorSeatModelDocumentStorageCompartment',
        title: 'ช่องเก็บเอกสารหลังเบาะคู่หน้า',
      },
      {
        fieldKey: 'interiorLuggageHanger',
        title: 'ที่แขวนสัมภาระหลังเบาะหน้า ด้านผู้โดยสาร',
      },
      { fieldKey: 'interiorSeatModelSeat2', title: 'เบาะนั่งตอน 2' },
      { fieldKey: 'interiorSeatModelSeat3', title: 'เบาะนั่งตอน 3' },
      { fieldKey: 'interiorAssistGrip', title: 'มือจับภายในรถ (Assist Grip)' },
    ],
  },
  {
    title: 'อุปกรณ์เพื่อความปลอดภัย',
    fieldKey: 'safety',
    items: [
      {
        fieldKey: 'safetyLedDaytimeRunningLight',
        title: 'ไฟส่องสว่างเวลากลางวันแบบ LED',
      },
      {
        fieldKey: 'safetyLedTurningSignal',
        title: 'ไฟเลี้ยวแบบ LED ที่กระจกมองข้าง',
      },
      { fieldKey: 'safetyTurningSignalLights', title: 'ไฟเลี้ยวด้านข้างตัวรถ' },
      { fieldKey: 'safety3rdBrakeLight', title: 'ไฟเบรกดวงที่ 3 แบบ LED' },
      {
        fieldKey: 'safetySteelBeamDoor',
        title: 'ประตูเสริมคานเหล็กเพื่อความปลอดภัย',
      },
      { fieldKey: 'safetyCollapsibleBrakePedal', title: 'แป้นเบรกยุบตัวได้' },
      {
        fieldKey: 'safetyFrontSideAirbags',
        title: 'ถุงลมคู่หน้า (Dual SRS Airbags)',
      },
      {
        fieldKey: 'safetySideAirbags',
        title: 'ถุงลมด้านข้างคู่หน้า (SRS Side Airbags)',
      },
      {
        fieldKey: 'safetySideCurtainAirbags',
        title: 'ม่านถุงลมด้านข้าง (SRS Roof Rail Airbags)',
      },
      {
        fieldKey: 'safetyDoorUnlockOnSrsAirbag',
        title: 'ระบบปลดล็อกประตูอัตโนมัติเมื่อถุงลม SRS ทำงาน',
      },
      {
        fieldKey: 'safetyOneMotionUnlock',
        title: 'ระบบปลดล็อกประตู One Motion Unlock เฉพาะด้านผู้ขับขี่',
      },
      { fieldKey: 'safetyFrontSeatBelt', title: 'เข็มขัดนิรภัยเบาะคู่หน้า' },
      { fieldKey: 'safetySeatBelt2', title: 'เข็มขัดนิรภัยเบาะตอน 2' },
      { fieldKey: 'safetySeatBelt3', title: 'เข็มขัดนิรภัยเบาะตอน 3' },
      {
        fieldKey: 'safetySeatBeltReminder',
        title: 'ระบบเตือนคาดเข็มขัดนิรภัยผู้โดยสารคู่หน้า',
      },
      {
        fieldKey: 'safetyIsofixMountingPoints',
        title: 'จุดยึด ISOFIX ที่เบาะตอน 2',
      },
      { fieldKey: 'safetyRearWindowDefogger', title: 'ไล่ฝ้ากระจกหลัง' },
      {
        fieldKey: 'safetyRearViewMirrorWithAntiGlare',
        title: 'กระจกมองหลังปรับแบบปรับลดแสงสะท้อนได้',
      },
      {
        fieldKey: 'safetyRearViewCamera',
        title: 'กล้องมองภาพด้านหลังขณะถอยจอด',
      },
      {
        fieldKey: 'safety360Camera',
        title: 'กล้องมองรอบคัน 360 องศา พร้อมมุมมองแบบ 3 มิติ',
      },
      {
        fieldKey: 'safetyElectricHandBrake',
        title: 'ระบบเบรกมือไฟฟ้า พร้อมระบบ Auto Brake Hold',
      },
      {
        fieldKey: 'safetyAbsWithEbdAndBa',
        title: 'ระบบป้องกันล้อล็อก ABS พร้อม EBD และ BA',
      },
      {
        fieldKey: 'safetyBrakeOverrideSystem',
        title: 'ระบบลดกำลังเครื่องยนต์เพื่อช่วยเบรก BOS',
      },
      {
        fieldKey: 'safetyEsc',
        title: 'ระบบควบคุมการทรงตัวอัตโนมัติ ESC',
      },
      {
        fieldKey: 'safetyTcs',
        title: 'ระบบป้องกันล้อหมุนฟรีขณะออกตัว TCS',
      },
      {
        fieldKey: 'safetyHsa',
        title: 'ระบบช่วยออกตัวบนทางลาดชัน HSA',
      },
      {
        fieldKey: 'safetyHdc',
        title: 'ระบบควบคุมความเร็วขณะลงทางลาดชัน HDC',
      },
      {
        fieldKey: 'safetyTsc',
        title: 'ระบบควบคุมการส่ายของส่วนพ่วงท้าย TSC (Trailer Sway Control)',
      },
      {
        fieldKey: 'safetyBsm',
        title: 'ระบบช่วยเตือนมุมอับสายตาที่กระจกมองข้าง BSM',
      },
      {
        fieldKey: 'safetyRctaRctb',
        title:
          'ระบบช่วยเตือนพร้อมระบบช่วยเบรกฉุกเฉินอัตโนมัติขณะถอย (RCTA & B)',
      },
      {
        fieldKey: 'safetyAttensionAssist',
        title: 'ระบบช่วยเตือนอาการเหนื่อยล้าขณะขับขี่ (Attention assist)',
      },
      {
        fieldKey: 'safety8PointsParkingSensors',
        title: 'เซ็นเซอร์ช่วยกะระยะหน้า 4 จุด และหลัง 4 จุด',
      },
      {
        fieldKey: 'safetyEssEmergencySignalSystem',
        title: 'สัญญาณไฟฉุกเฉินอัตโนมัติขณะเบรกกะทันหัน ESS',
      },
      {
        fieldKey: 'safetyWalkAwayAutoDoorLock',
        title:
          'ระบบล็อกรถอัตโนมัติเมื่อกุญแจรีโมทอยู่ห่างจากตัวรถ (Walk Away Auto Lock)',
      },
      { fieldKey: 'safetyAntiTheftAlarm', title: 'สัญญาณกันขโมย' },
      {
        fieldKey: 'safetyWarningTurnOffHeadlights',
        title: 'สัญญาณเสียงเตือนปิดไฟหน้า',
      },
      { fieldKey: 'safetyAlarmForgetKey', title: 'สัญญาณเสียงเตือนลืมกุญแจ' },
      {
        fieldKey: 'safetyFcw',
        title: 'ระบบแจ้งเตือนก่อนการชนด้านหน้า Forward Collision Warning (FCW)',
      },
      {
        fieldKey: 'safetyAeb',
        title: 'ระบบช่วยเบรกฉุกเฉินอัตโนมัติ (AEB)',
      },
      {
        fieldKey: 'safetyLdw',
        title: 'ระบบแจ้งเตือนเมื่อรถออกนอกเลน Lane Departure Warning (LDW)',
      },
      {
        fieldKey: 'safetyLdp',
        title:
          'ระบบช่วยควบคุมรถไม่ให้ออกนอกเลน Lane Departure Prevention (LDP)',
      },
      {
        fieldKey: 'safetyElk',
        title:
          'ระบบช่วยควบคุมรถให้อยู่ในเลนในสถานการณ์ฉุกเฉิน Emergency Lane Keeping (ELK)',
      },
      {
        fieldKey: 'interiorCruiseControl',
        title: 'ระบบควบคุมความเร็วอัตโนมัติ (Cruise Control)',
      },
      {
        fieldKey: 'safetyLkas',
        title: 'ระบบช่วยควบคุมรถให้อยู่ในเลน (LKAS)',
      },
      {
        fieldKey: 'safetyTja',
        title: 'ระบบช่วยควบคุมทิศทางของรถตามรถคันหน้า (TJA)',
      },
      {
        fieldKey: 'safetyPmm',
        title:
          'ระบบช่วยตัดกำลังเครื่องยนต์เมื่อเหยียบคันเร่งผิดพลาด Pedal Misapplication Mitigation (PMM)',
      },
      {
        fieldKey: 'safetyMsl',
        title:
          'ระบบตั้งค่าจำกัดความเร็วสูงสุดด้วยตัวเอง Manual Speed Limiter (MSL)',
      },
      {
        fieldKey: 'safetyAhb',
        title: 'ระบบควบคุมไฟสูงอัตโนมัติ Automatic High Beam (AHB)',
      },
      {
        fieldKey: 'safetyMcb',
        title:
          'ระบบช่วยเบรกอัตโนมัติหลังการเกิดอุบัติเหตุ Multi-Collision Brake (MCB)',
      },
      {
        fieldKey: 'safetyTurnAssist',
        title:
          'ระบบช่วยเบรกฉุกเฉินอัตโนมัติเมื่อมีรถสวนทางขณะเลี้ยวขวา (TA-AEB)',
      },
    ],
  },
];
